import React, { useContext, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Container,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { motion } from "framer-motion";
import {
  IconArrowRight,
  IconCheck,
  IconMail,
  IconMapPin,
  IconPhone,
  IconUser,
} from "@tabler/icons-react";
import { AllFatherContext } from "../../App";
import { HttpErrorCodesObj, HttpSuccessCodesObj } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  validateEmail,
} from "../../Lib/methods";
import { NonPaginatedResponse } from "../../Lib/responses";
import { getSiteName } from "../../Lib/siteContent";
import { PerformRequest } from "../../Lib/usePerformRequest";
import {
  countryCallingCodeOptions,
  countryOptions,
} from "../../Lib/countryCallingCodes";
import LanguageSelector from "../../Lib/i18n/LanguageSelector";
import "./styles.scss";

const featureItems = [
  "Get notified when admissions or cohort slots open",
  "Receive updates about upcoming programs and orientation windows",
  "Share your details once and let the team reach out when ready",
];

const Join: React.FC = () => {
  const context = useContext(AllFatherContext);
  const siteName = getSiteName(context?.assets);
  const [isLoading, setLoading] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const joinForm = useForm({
    initialValues: {
      name: "",
      email: "",
      country: "US",
      countryCode: "US",
      phoneNumber: "",
    },
    validate: {
      name: (value) => (value.trim().length < 3 ? "Full name is required" : null),
      email: (value) => (validateEmail(value) ? null : "Enter a valid email"),
      country: (value) => (!value ? "Country is required" : null),
      countryCode: (value) => (!value ? "Country code is required" : null),
      phoneNumber: (value) =>
        value.replace(/\D/g, "").length < 7
          ? "Enter a valid phone number"
          : null,
    },
  });

  const handleJoin = async () => {
    const { hasErrors } = joinForm.validate();
    if (hasErrors) {
      return;
    }

    setLoading(true);
    const selectedCountry = countryOptions.find(
      (option) => option.value === joinForm.values.country
    );
    const selectedCountryCode = countryCallingCodeOptions.find(
      (option) => option.value === joinForm.values.countryCode
    );

    const response = await PerformRequest<
      NonPaginatedResponse<{ email: string; setupLinkSent: boolean }>
    >({
      method: Endpoints.GetStudentJoin().method,
      route: Endpoints.GetStudentJoin().route,
      data: {
        name: joinForm.values.name,
        email: joinForm.values.email,
        country: selectedCountry?.label ?? joinForm.values.country,
        phone: `${selectedCountryCode?.code ?? "+1"}${joinForm.values.phoneNumber.replace(
          /\D/g,
          ""
        )}`,
      },
      callback: () => {
        setLoading(false);
      },
    });
    setLoading(false);

    if (response?.status === HttpSuccessCodesObj.Created) {
      setSubmittedEmail(joinForm.values.email);
      setHasJoined(true);
      return;
    }

    if (response?.status === HttpErrorCodesObj.Conflict) {
      DefaultErrorNotification("This email is already on the waitlist or already registered.");
      return;
    }

    DefaultErrorNotification(
      response?.data?.message ?? "Could not complete your join request."
    );
  };

  return (
    <div className="join-page">
      <div className="join-orb join-orb-a"></div>
      <div className="join-orb join-orb-b"></div>
      <div className="join-grid"></div>

      <div style={{ position: "fixed", top: 24, right: 24, zIndex: 20 }}>
        <LanguageSelector />
      </div>

      <Container size="xl" className="join-shell">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="join-layout"
        >
          <div className="join-copy">
            <div className="join-brand">
              <img
                src={context?.assets?.logos?.legacy}
                alt={siteName}
                className="join-logo"
              />
              <Text className="join-kicker">Waitlist</Text>
            </div>

            <Title className="join-title">
              Join the {siteName} waitlist
            </Title>
            <Text className="join-subtitle">
              Leave your details and the team can contact you when the right
              intake, class, or onboarding window is available.
            </Text>

            <Stack gap="sm" mt="xl">
              {featureItems.map((item) => (
                <Card key={item} className="join-feature" radius="xl">
                  <Group wrap="nowrap" align="flex-start">
                    <ThemeIcon radius="xl" size={38} variant="light" color="teal">
                      <IconCheck size={18} />
                    </ThemeIcon>
                    <Text fw={600}>{item}</Text>
                  </Group>
                </Card>
              ))}
            </Stack>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Paper className="join-card" radius="32px" p="xl">
              {hasJoined ? (
                <Stack gap="lg">
                  <ThemeIcon size={68} radius="xl" color="teal" variant="light">
                    <IconCheck size={34} />
                  </ThemeIcon>
                  <div>
                    <Title order={2}>You’re on the waitlist</Title>
                    <Text c="dimmed" mt="xs">
                      Your details have been received for <strong>{submittedEmail}</strong>.
                    </Text>
                  </div>
                  <Alert color="teal" radius="xl" variant="light">
                    The admissions team will contact you with next steps when a
                    suitable slot or onboarding path is available.
                  </Alert>
                </Stack>
              ) : (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleJoin();
                  }}
                >
                  <Stack gap="lg">
                    <div>
                      <Title order={2}>Join the waitlist</Title>
                      <Text c="dimmed" mt={6}>
                        Full name, email, phone number, and country are all we need.
                      </Text>
                    </div>

                    <TextInput
                      label="Full name"
                      placeholder="Enter your full name"
                      size="md"
                      radius="xl"
                      leftSection={<IconUser size={16} />}
                      {...joinForm.getInputProps("name")}
                    />

                    <TextInput
                      label="Email"
                      placeholder="you@example.com"
                      size="md"
                      radius="xl"
                      leftSection={<IconMail size={16} />}
                      {...joinForm.getInputProps("email")}
                    />

                    <Select
                      searchable
                      label="Country"
                      placeholder="Select your country"
                      size="md"
                      radius="xl"
                      leftSection={<IconMapPin size={16} />}
                      data={countryOptions}
                      nothingFoundMessage="No country found"
                      {...joinForm.getInputProps("country")}
                      onChange={(value) => {
                        joinForm.setFieldValue("country", value || "");
                        if (value) {
                          joinForm.setFieldValue("countryCode", value);
                        }
                      }}
                    />

                    <Group grow align="flex-start" wrap="nowrap">
                      <Select
                        searchable
                        label="Code"
                        placeholder="+1"
                        size="md"
                        radius="xl"
                        data={countryCallingCodeOptions.map((option) => ({
                          value: option.value,
                          label: option.code,
                        }))}
                        nothingFoundMessage="No code found"
                        {...joinForm.getInputProps("countryCode")}
                      />
                      <TextInput
                        label="Phone number"
                        placeholder="Enter your phone number"
                        size="md"
                        radius="xl"
                        leftSection={<IconPhone size={16} />}
                        {...joinForm.getInputProps("phoneNumber")}
                      />
                    </Group>

                    <Button
                      type="submit"
                      size="lg"
                      radius="xl"
                      loading={isLoading}
                      rightSection={<IconArrowRight size={18} />}
                      className="join-submit"
                    >
                      Join waitlist
                    </Button>
                  </Stack>
                </form>
              )}
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </div>
  );
};

export default Join;
